import { useState, useEffect } from "react";
import { shopPath } from "../lib/paths";
import { thumb } from "../lib/img";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Marquee from "react-fast-marquee";
import { MapPin, ArrowRight, Star, Truck, ShoppingBag, Store, Sparkles, CalendarHeart, Bell } from "lucide-react";
import { Button } from "../components/ui/button";
import DeliverySearchBar from "../components/DeliverySearchBar";
import { InstallBanner } from "../components/InstallBanner";
import { ExpressBadge } from "../components/ExpressBadge";
import { CatalogProductCard } from "../components/CatalogProductCard";
import { Seo, orgJsonLd, websiteJsonLd } from "../components/Seo";
import { MOMENTS, momentById } from "../lib/moments";
import { PER_ME_QUESTION, PER_ME_DESCRIPTION, PER_ME_CTA, PER_ME_ENABLED } from "../lib/perMe";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/9e22f6af-067e-45c3-9f23-efc21196c739/images/75c6398e84492260156ebf46c4a20b06ae29d9509291cfca515866d5e32682e9.png";
const HERO_IMAGE_MOBILE = "/hero-mobile.jpg";

const CATEGORIES = [
  "Rose", "Fiori", "Orchidee", "Piante", "Condoglianze", "Luxury", "Auguri", "Servizi", "Accessori"
];

export default function HomePage() {
  const [moments, setMoments] = useState(MOMENTS.filter(m => PER_ME_ENABLED || m.id !== "per_me").map(m => ({ id: m.id, label: m.label, featured: false })));
  useEffect(() => {
    axios.get(`${API}/products/moments`).then(r => setMoments((r.data.moments || []).filter(m => PER_ME_ENABLED || m.id !== "per_me"))).catch(() => {});
  }, []);
  const [shops, setShops] = useState([]);
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchShops();
    fetchProducts();
  }, []);

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API}/shops`);
      setShops(response.data.slice(0, 4));
    } catch (error) {
      console.error("Error fetching shops:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/catalog-public`);
      setProducts(response.data.featured || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  return (
    <div className="min-h-screen">
      <Seo title="Flowerlive — Fiori a domicilio dal fiorista vicino a te" description="Ordina fiori a domicilio in tutta Italia: bouquet, piante e composizioni floreali preparate dal fiorista affiliato più vicino e consegnate in giornata." path="/" jsonLd={[orgJsonLd(), websiteJsonLd()]} />
      <InstallBanner />
      {/* Hero Section */}
      <section className="relative z-20 min-h-[92svh] md:min-h-0 md:h-[85vh] bg-[#141a10]" data-testid="hero-section">
        <div
          className="absolute inset-0 overflow-hidden bg-[length:auto_120%] bg-[center_bottom] md:hidden"
          style={{ backgroundImage: `url(${HERO_IMAGE_MOBILE})` }}
          data-testid="hero-bg-mobile"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d120a]/85 via-[#0d120a]/35 to-[#0d120a]/95" />
        </div>
        <div
          className="absolute inset-0 overflow-hidden bg-cover bg-center hidden md:block"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          data-testid="hero-bg-desktop"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-[92svh] md:min-h-0 md:h-full flex flex-col justify-end md:justify-center pb-10 pt-28 md:py-0">
          <div className="max-w-2xl">
            <ExpressBadge variant="dark" className="mb-5 animate-in fade-in slide-in-from-bottom-2 duration-700" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tighter text-white leading-tight mb-5 md:mb-6 [text-shadow:0_2px_24px_rgba(0,0,0,.45)]">
              Sorprendi chi ami con <span className="italic">fiori freschi</span> a casa sua
            </h1>
            <p className="text-base md:text-lg text-white/85 font-sans leading-relaxed mb-6 md:mb-8 [text-shadow:0_1px_12px_rgba(0,0,0,.5)]">
              Il fiorista della zona prepara e consegna a mano. Nessuno può farlo in 2 ore: il tuo fiorista sì.
            </p>

            <div className="max-w-xl" data-testid="hero-search">
              <DeliverySearchBar variant="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* MOMENTI */}
      <section className="py-14 md:py-20 px-6 md:px-12 max-w-7xl mx-auto" data-testid="moments-section">
        <div className="mb-8">
          <span className="text-xs font-sans uppercase tracking-[0.2em] text-brand-pink font-bold">Scegli il momento</span>
          <h2 className="text-2xl sm:text-3xl font-serif tracking-tight text-forest mt-2">Non cerchi un fiore. Cerchi il gesto giusto.</h2>
          <p className="text-sage mt-2 max-w-xl text-sm">Dicci cosa vuoi dire e ti mostriamo le composizioni pensate per quel momento, preparate dal fiorista più vicino a chi le riceve.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {moments.map((m, i) => {
            const meta = momentById(m.id) || MOMENTS[0];
            const Icon = meta.icon;
            return (
              <button
                key={m.id}
                onClick={() => navigate(`/momenti/${m.id}`)}
                className={`group relative text-left bg-white rounded-2xl border p-4 md:p-5 hover:shadow-xl hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300 ${m.featured ? "border-brand-pink/40 shadow-md col-span-2 md:col-span-1" : "border-[#7A8C69]/10"}`}
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`moment-${m.id}`}
                data-featured={m.featured ? "true" : "false"}
              >
                {m.featured && (
                  <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.12em] font-bold text-brand-pink bg-[#FBE9F0] px-2 py-0.5 rounded-full" data-testid="moment-season-badge">In questo periodo</span>
                )}
                <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${meta.tone} group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="h-5 w-5" />
                </span>
                <p className="font-serif text-base md:text-lg text-forest mt-3 leading-tight">{m.label}</p>
                <p className="text-[11px] md:text-xs text-sage mt-1">{m.featured && m.season_label ? m.season_label : meta.hint}</p>
              </button>
            );
          })}
        </div>
      </section>

      {PER_ME_ENABLED && <section className="px-6 md:px-12 max-w-7xl mx-auto pb-6" data-testid="per-me-teaser">
        <div className="relative rounded-3xl overflow-hidden min-h-[380px] md:min-h-[440px] flex items-end">
          <img src="/per-me/hero.jpg" alt="Fiori freschi sul tavolo di casa" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2b1d14]/85 via-[#2b1d14]/35 to-transparent" />
          <div className="relative p-8 md:p-12 text-white max-w-2xl">
            <p className="text-xs tracking-[0.3em] uppercase text-[#F3C9A8] mb-3">Fiori per me</p>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight">{PER_ME_QUESTION}</h2>
            <p className="text-white/85 mt-4 leading-relaxed">{PER_ME_DESCRIPTION}</p>
            <Link to="/fiori-per-me" className="inline-flex items-center gap-2 mt-6 bg-[#B85C38] hover:bg-[#9c4a2c] text-white px-6 py-3 rounded-full transition-colors" data-testid="per-me-cta">{PER_ME_CTA} →</Link>
          </div>
        </div>
      </section>}

      {/* ORDINE VELOCE — Quick Order */}
      {products.length > 0 && (
        <section className="py-16 px-6 md:px-12 max-w-7xl mx-auto" data-testid="ordine-veloce">
          <div className="text-center mb-10">
            <span className="text-xs font-sans uppercase tracking-[0.2em] text-brand-pink font-bold">
              Ordine Veloce
            </span>
            <h2 className="text-2xl sm:text-3xl font-serif tracking-tight text-forest mt-2">
              Scegli, ordina e paga in 2 minuti.
            </h2>
            <p className="text-sage mt-2 max-w-lg mx-auto text-sm">
              I bouquet piu amati, pronti per essere inviati. Clicca, scegli la misura e completa l'ordine subito.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.slice(0, 4).map((product) => (
              <div
                key={product.id}
                className="group bg-white rounded-2xl overflow-hidden border border-[#7A8C69]/10 hover:shadow-xl transition-all duration-300 cursor-pointer"
                onClick={() => navigate(`/products/${product.id}`)}
                data-testid={`quick-order-${product.id}`}
              >
                <div className="aspect-square overflow-hidden relative bg-[#F5F0E8]">
                  {product.images?.[0] ? (
                    <img
                      src={thumb(product.images[0], 480)}
                      alt={product.name}
                      loading="lazy" decoding="async"
                      className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingBag className="h-12 w-12 text-sage-light" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] font-semibold text-[#2D5016]">
                      {product.category}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
                    <p className="text-white text-xs font-medium">
                      {product.available_sizes?.length > 1 ? `${product.available_sizes.length} misure disponibili` : "Misura unica"}
                    </p>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-serif text-base text-[#2D5016] line-clamp-1 group-hover:text-brand-pink transition-colors">
                    {product.name}
                  </h3>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-serif text-lg text-[#2D5016]">
                      da {"\u20AC"}{product.display_price?.toFixed(2) || product.price?.toFixed(2)}
                    </span>
                    <span className="bg-[#2D5016] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full group-hover:bg-[#223E11] transition-colors">
                      Ordina
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button
              onClick={() => navigate("/catalogo")}
              variant="outline"
              className="rounded-full px-8 py-5 border-[#2D5016] text-[#2D5016] hover:bg-[#2D5016] hover:text-white transition-all"
              data-testid="view-all-products-btn"
            >
              Vedi tutto il catalogo
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </section>
      )}

      {/* Category Marquee */}
      <section className="bg-cream-dark py-4" data-testid="category-marquee">
        <Marquee gradient={false} speed={40}>
          {[...CATEGORIES, ...CATEGORIES].map((cat, i) => (
            <span key={`${cat}-${i < CATEGORIES.length ? "a" : "b"}`} className="mx-8 text-sm font-sans uppercase tracking-[0.15em] text-forest/70">
              {cat}
            </span>
          ))}
        </Marquee>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto" data-testid="how-it-works-section">
        <div className="text-center mb-16">
          <span className="text-xs font-sans uppercase tracking-[0.2em] text-brand-pink font-bold">
            Come Funziona
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-forest leading-tight mt-2">
            Tre semplici passi per i tuoi fiori
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            {
              icon: <MapPin className="h-8 w-8" />,
              title: "Inserisci l'Indirizzo",
              description: "Scrivi la citta e l'indirizzo di chi vuoi sorprendere. Troveremo il fiorista piu vicino, pronto a comporre il tuo bouquet."
            },
            {
              icon: <img src="/logo-icon.png" alt="" className="h-8 w-8" />,
              title: "Scegli il Bouquet",
              description: "Sfoglia i fiori disponibili, personalizza il messaggio sul bigliettino e scegli l'orario di consegna ideale."
            },
            {
              icon: <Truck className="h-8 w-8" />,
              title: "Noi Consegniamo per Te",
              description: "Il fiorista prepara il tuo omaggio floreale e lo consegna direttamente nelle mani del destinatario. Tu fai bella figura."
            }
          ].map((step) => (
            <div key={step.title} className="text-center group">
              <div className="w-16 h-16 rounded-full bg-cream-dark flex items-center justify-center mx-auto mb-6 text-brand group-hover:bg-brand group-hover:text-white transition-all duration-300">
                {step.icon}
              </div>
              <h3 className="text-xl font-serif text-forest mb-3">{step.title}</h3>
              <p className="text-sage font-sans leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Shops */}
      {shops.length > 0 && (
        <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto" data-testid="featured-shops-section">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-xs font-sans uppercase tracking-[0.2em] text-brand-pink font-bold">
                Fioristi in Evidenza
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-forest leading-tight mt-2">
                I nostri fioristi partner
              </h2>
            </div>
            <Link 
              to="/shops"
              className="hidden sm:flex items-center gap-2 text-forest hover:text-forest-light transition-colors font-medium"
              data-testid="view-all-shops-link"
            >
              Vedi tutti
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {shops.map((shop) => (
              <Link 
                key={shop.id} 
                to={shopPath(shop)}
                className="group"
                data-testid={`shop-card-${shop.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden mb-4 bg-[#F5F0E8]">
                  {shop.cover_url ? (
                    <img 
                      src={thumb(shop.cover_url, 800)}
                      alt={shop.name}
                      loading="lazy" decoding="async"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Store className="h-12 w-12 text-[#7A8C69]/30" />
                    </div>
                  )}
                </div>
                <h3 className="font-serif text-lg text-forest group-hover:text-forest-light transition-colors">
                  {shop.name}
                </h3>
                <p className="text-sm text-sage-light flex items-center gap-1 mt-1">
                  <MapPin className="h-3 w-3" />
                  {shop.city}
                </p>
              </Link>
            ))}
          </div>

          <div className="sm:hidden mt-8 text-center">
            <Link 
              to="/shops"
              className="inline-flex items-center gap-2 text-forest hover:text-forest-light transition-colors font-medium"
            >
              Vedi tutti i fioristi
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {/* Featured Products */}
      {products.length > 0 && (
        <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto bg-white" data-testid="featured-products-section">
          <div className="text-center mb-12">
            <span className="text-xs font-sans uppercase tracking-[0.2em] text-brand-pink font-bold">
              Prodotti in Evidenza
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-forest leading-tight mt-2">
              Le nostre creazioni floreali
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-6" data-testid="featured-products-grid">
            {products.slice(0, 5).map((product, i) => (
              <CatalogProductCard key={product.id} product={product} className={i === 4 ? "hidden xl:block" : ""} />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/catalogo" data-testid="view-full-catalog-btn">
              <Button variant="outline" className="rounded-full border-forest text-forest hover:bg-forest hover:text-white px-8 py-6 text-base">
                Guarda l'intero catalogo
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <p className="text-xs text-sage mt-3">Piante · Fiori e composizioni · Prodotti funebri</p>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 bg-forest" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif tracking-tight text-white leading-tight mb-6">
            Hai una fioreria? <br />
            <span className="italic">Fiorisci online con noi</span>
          </h2>
          <p className="text-lg text-white/80 font-sans leading-relaxed mb-8 max-w-2xl mx-auto">
            Tu gestisci il negozio, noi ti portiamo i clienti. Unisciti al marketplace Flowerlive e raggiungi nuovi clienti ogni giorno.
          </p>
          <Button 
            onClick={() => navigate("/apri-negozio")}
            className="bg-brand text-white hover:bg-brand-dark px-8 py-6 text-lg"
            data-testid="become-florist-button"
          >
            Diventa Partner
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Trust Badges */}
      {/* SORPRESA PROGRAMMATA */}
      <section className="py-16 px-6 md:px-12 max-w-7xl mx-auto" data-testid="ricorrenze-section">
        <div className="bg-white rounded-3xl border border-[#7A8C69]/10 p-7 md:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 shadow-sm">
          <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#FBE9F0] text-[#A0416B]">
            <CalendarHeart className="h-7 w-7" />
          </span>
          <div className="flex-1">
            <span className="text-xs font-sans uppercase tracking-[0.2em] text-brand-pink font-bold">Sorpresa programmata</span>
            <h2 className="text-2xl sm:text-3xl font-serif tracking-tight text-forest mt-1">"Ricordamelo ogni anno il 14 febbraio"</h2>
            <p className="text-sage text-sm mt-2 max-w-2xl">Salva le date che contano: qualche giorno prima ti scriviamo noi e con un solo click rimandi gli stessi fiori allo stesso indirizzo. Nessuna dimenticanza, nessun addebito automatico.</p>
          </div>
          <Button onClick={() => navigate("/ricorrenze")} className="bg-forest text-white hover:bg-forest-light rounded-full px-7 py-5 shrink-0" data-testid="cta-ricorrenze">
            <Bell className="h-4 w-4 mr-2" /> Imposta un promemoria
          </Button>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 max-w-7xl mx-auto" data-testid="trust-badges-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { value: "15km", label: "Raggio di consegna" },
            { value: "2h", label: "Consegna express in zona" },
            { value: "100%", label: "Fiori freschi" },
            { value: "4.9", label: "Valutazione media", icon: <Star className="h-4 w-4 inline text-brand" /> }
          ].map((stat) => (
            <div key={stat.label}>
              <p className="text-3xl font-serif text-forest flex items-center justify-center gap-1">
                {stat.value} {stat.icon}
              </p>
              <p className="text-sm text-sage-light mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
